import { useState, useEffect } from 'react'
import { createClient } from '@/lib/supabase-client'

interface APIKey {
  id: string
  api_key: string
  name: string | null
  last_used_at: string | null
  created_at: string
  usage_stats?: {
    total_usage: number
    credits_used: number
    last_used_at: string | null
  }
}

interface Props {
  onUpdate?: () => void
}

export default function MultipleAPIKeysSection({ onUpdate }: Props) {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null)
  const [showingKeyId, setShowingKeyId] = useState<string | null>(null)
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null)
  const supabase = createClient()

  useEffect(() => {
    loadAPIKeys()
  }, [])

  const loadAPIKeys = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      // Load API keys
      const { data: keysData, error: keysError } = await supabase
        .from('api_keys')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })

      if (keysError) {
        console.error('Error loading API keys:', keysError)
        setLoading(false)
        return
      }

      if (!keysData || keysData.length === 0) {
        setApiKeys([])
        setLoading(false)
        return
      }

      // Get usage stats for all keys
      const monthStart = new Date()
      monthStart.setDate(1)
      monthStart.setHours(0, 0, 0, 0)

      const { data: usageData, error: usageError } = await supabase.rpc(
        'get_user_api_keys_usage',
        {
          p_user_id: session.user.id,
          p_month_start: monthStart.toISOString()
        }
      )

      if (usageError) {
        console.error('Error loading usage stats:', usageError)
        // Still show keys without usage stats
        setApiKeys(keysData)
        setLoading(false)
        return
      }

      // Merge usage stats with API keys
      const keysWithUsage: APIKey[] = keysData.map(key => {
        const usage = usageData?.find((u: any) => u.api_key_id === key.id)
        return {
          ...key,
          usage_stats: usage ? {
            total_usage: usage.total_usage || 0,
            credits_used: usage.credits_used || 0,
            last_used_at: usage.last_used_at
          } : {
            total_usage: 0,
            credits_used: 0,
            last_used_at: null
          }
        }
      })

      setApiKeys(keysWithUsage)
    } catch (error) {
      console.error('Error loading API keys:', error)
    } finally {
      setLoading(false)
    }
  }

  const createAPIKey = async () => {
    if (!newKeyName.trim()) {
      alert('Please enter a name for the API key')
      return
    }

    setCreating(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      // Generate new API key
      const newKey = 'atg_' + Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('')

      const { data, error } = await supabase
        .from('api_keys')
        .insert({
          user_id: session.user.id,
          api_key: newKey,
          name: newKeyName.trim()
        })
        .select()
        .single()

      if (error) {
        alert('Failed to create API key. Please try again.')
        console.error('Error creating API key:', error)
      } else {
        setNewKeyName('')
        await loadAPIKeys()
        if (onUpdate) onUpdate()
        // Show the newly created key
        if (data) {
          setShowingKeyId(data.id)
          alert('API key created successfully! Make sure to copy it now - you won\'t be able to see it again.')
        }
      }
    } catch (error) {
      console.error('Error creating API key:', error)
      alert('Failed to create API key. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  const deleteAPIKey = async (keyId: string, keyName: string) => {
    if (!confirm(`Are you sure you want to delete "${keyName}"? This action cannot be undone and any integrations using this key will stop working.`)) {
      return
    }

    setDeletingKeyId(keyId)
    try {
      const { error } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', keyId)

      if (error) {
        alert('Failed to delete API key. Please try again.')
        console.error('Error deleting API key:', error)
      } else {
        await loadAPIKeys()
        if (onUpdate) onUpdate()
      }
    } catch (error) {
      console.error('Error deleting API key:', error)
      alert('Failed to delete API key. Please try again.')
    } finally {
      setDeletingKeyId(null)
    }
  }

  const copyToClipboard = (key: string, keyId: string) => {
    navigator.clipboard.writeText(key)
    setCopiedKeyId(keyId)
    setTimeout(() => setCopiedKeyId(null), 2000)
  }

  const maskKey = (key: string) => {
    if (key.length <= 12) return '•'.repeat(key.length)
    return key.substring(0, 8) + '•'.repeat(56) + key.substring(64)
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  if (loading) {
    return (
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Create New API Key Section */}
      <div className="glass-card rounded-xl p-6 hover-lift">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create New API Key
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Key Name
            </label>
            <input
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="e.g., Production, Development, Shopify Integration"
              className="w-full px-4 py-3 border border-gray-300/50 dark:border-slate-600/50 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Give your API key a descriptive name to help you identify its purpose.
            </p>
          </div>
          <button
            onClick={createAPIKey}
            disabled={creating || !newKeyName.trim()}
            className="w-full px-4 py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition hover-lift flex items-center justify-center space-x-2"
          >
            {creating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Creating...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Create API Key</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* API Keys List */}
      <div className="glass-card rounded-xl p-6 hover-lift">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            <svg className="w-6 h-6 mr-2 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            Your API Keys ({apiKeys.length})
          </h3>
        </div>

        {apiKeys.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400">No API keys yet. Create your first one above.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {apiKeys.map((key) => (
              <div
                key={key.id}
                className="border border-gray-200/50 dark:border-slate-700/50 rounded-lg p-4 hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {key.name || 'Unnamed Key'}
                      </h4>
                      {key.usage_stats && key.usage_stats.total_usage > 0 && (
                        <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                        <span>Created: {formatDate(key.created_at)}</span>
                        <span>Last used: {formatDate(key.usage_stats?.last_used_at || key.last_used_at)}</span>
                      </div>
                      {key.usage_stats && (
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="text-gray-600 dark:text-gray-300">
                            <span className="font-medium">{key.usage_stats.total_usage}</span> requests this month
                          </span>
                          {key.usage_stats.credits_used > 0 && (
                            <span className="text-gray-600 dark:text-gray-300">
                              <span className="font-medium">{key.usage_stats.credits_used}</span> credits used
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteAPIKey(key.id, key.name || 'Unnamed Key')}
                    disabled={deletingKeyId === key.id}
                    className="ml-4 p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition disabled:opacity-50"
                    title="Delete API key"
                  >
                    {deletingKeyId === key.id ? (
                      <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={showingKeyId === key.id ? key.api_key : maskKey(key.api_key)}
                      readOnly
                      className="w-full px-4 py-2 border border-gray-300/50 dark:border-slate-600/50 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm text-gray-900 dark:text-white font-mono text-sm rounded-lg"
                    />
                    {copiedKeyId === key.id && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1 text-green-600 dark:text-green-400 animate-fade-in">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs font-medium">Copied!</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setShowingKeyId(showingKeyId === key.id ? null : key.id)}
                    className="px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition"
                  >
                    {showingKeyId === key.id ? 'Hide' : 'Show'}
                  </button>
                  <button
                    onClick={() => copyToClipboard(key.api_key, key.id)}
                    className="px-3 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 text-sm font-medium transition flex items-center space-x-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy</span>
                  </button>
                </div>
                {showingKeyId === key.id && (
                  <p className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-start">
                    <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>Make sure to copy this key now. You won't be able to see the full key again after refreshing the page.</span>
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

